import {
  actionColumn,
  ButtonMenu,
  collapseColumn,
  CollapseToggle,
  column,
  Css,
  emptyCell,
  getTableStyles,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  HeaderAction,
  Icon,
  InlineFeedbackBanner,
  Loader,
  Palette,
  RowStyles,
  Tooltip,
  useGridTableApi,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useEffectOnce } from "react-use";
import { createMilestoneDetailsPageUrl } from "src/RouteUrls";
import { emptyCellDash, formatDate, Icon as LegacyBpIcon, ProgressPill } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { TaskStatus } from "src/generated/graphqlTypes";
import { pluralize } from "src/utils";
import { formatWithShortYear } from "src/utils/dates";
import { DraftScheduleLayoutState, TaskGroupBy } from "../components/draftScheduleFilters";
import { DynamicSchedulesRow, DynamicSchedulesTaskRow } from "../utils";
import {
  createRows,
  dateSortHelper,
  durationSortHelper,
  hasGroupRows,
  maybeRenderColumnForParentTypes,
  MilestoneActionCell,
  nameSortHelper,
  TextTruncatedTooltip,
  tradeStatusSortHelper,
  useDraftTaskMenuItems,
} from "./DraftScheduleTable";
import { useDraftTaskDrawer } from "./DraftTaskDrawer";
import { DraftTradeStatusTag } from "./DraftTradeStatusTag";
import {
  DraftModeEnumDetails_EnumDetailsFragment,
  ScheduleDraftMode_PlanMilestoneFragment,
  ScheduleDraftMode_PlanTaskFragment,
  ScheduleDraftMode_ScheduleParentFragment,
} from "./ScheduleDraftMode.generated";
import { DraftTaskAlert, getDraftTaskAlerts } from "./draftTaskAlerts";

type DraftScheduleTableV2Props = {
  planTasks: ScheduleDraftMode_PlanTaskFragment[];
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[];
  enumDetails: DraftModeEnumDetails_EnumDetailsFragment;
  loading: boolean;
  groupBy: TaskGroupBy;
  scheduleParentId: string;
  hasDraftChanges: boolean;
  scheduleParent: ScheduleDraftMode_ScheduleParentFragment | undefined;
  allTasks?: ScheduleDraftMode_PlanTaskFragment[];
  layoutState: DraftScheduleLayoutState;
  actions: HeaderAction[];
};

/**
 * The read-only draft schedule table, behind `DYNAMIC_SCHEDULES_V2`.
 *
 * Clicking a task opens the task drawer, which is where tasks are edited. Tasks that need attention get alert rows.
 */
export function DraftScheduleTableV2(props: DraftScheduleTableV2Props) {
  const {
    planTasks,
    planMilestones,
    enumDetails,
    loading,
    groupBy,
    scheduleParentId,
    hasDraftChanges,
    scheduleParent,
    allTasks,
    layoutState,
    actions,
  } = props;
  const isLookaheadView = groupBy === TaskGroupBy.Lookahead;
  const navigate = useNavigate();
  const api = useGridTableApi<DynamicSchedulesRow>();
  const { openDraftTaskDrawer, paneTaskId, openTaskId } = useDraftTaskDrawer();

  const openMilestone = useCallback(
    (milestoneId: string) => navigate(createMilestoneDetailsPageUrl(scheduleParentId, milestoneId)),
    [navigate, scheduleParentId],
  );

  const rows = useMemo(
    () => withAlertRows(createRows(planTasks, planMilestones, groupBy, allTasks ?? [])),
    [planTasks, planMilestones, groupBy, allTasks],
  );

  const columns = useMemo(
    () =>
      createColumns({
        loading,
        enumDetails,
        groupBy,
        isLookaheadView,
        hasDraftChanges,
        parentType: scheduleParent?.__typename,
      }),
    [loading, enumDetails, groupBy, isLookaheadView, hasDraftChanges, scheduleParent?.__typename],
  );

  const rowStyles: RowStyles<DynamicSchedulesRow> = useMemo(
    () => ({
      lookahead: { cellCss: Css.bgGray100.$ },
      milestone: { onClick: (row) => openMilestone(row.id) },
      task: { cellCss: (task) => taskRowCss(task), onClick: (row) => openDraftTaskDrawer(row.id) },
    }),
    [openDraftTaskDrawer, openMilestone],
  );

  const styles = useMemo(() => getTableStyles({ grouped: hasGroupRows(groupBy), allWhite: true }), [groupBy]);

  // A link with `?paneTaskId=` opens that task's drawer
  useEffectOnce(() => {
    if (paneTaskId && planTasks.some((t) => t.id === paneTaskId)) openDraftTaskDrawer(paneTaskId);
  });

  return (
    <GridTableLayout
      key={isLookaheadView ? "lookahead-schedule" : "list-schedule"}
      hideEditColumns
      layoutState={layoutState}
      actions={actions}
      withRightPane
      emptyFallback="No tasks found"
      tableProps={{
        as: "virtual",
        api,
        columns,
        rows,
        rowStyles,
        activeRowId: openTaskId ? `task_${openTaskId}` : undefined,
        style: styles,
        sorting: { on: "client", initial: ["start-date-column", "ASC"] },
      }}
    />
  );
}

type CreateColumnsOpts = {
  loading: boolean;
  enumDetails: DraftModeEnumDetails_EnumDetailsFragment;
  groupBy: TaskGroupBy;
  // Lookahead dividers are in a fixed order, so the lookahead view can't be sorted
  isLookaheadView: boolean;
  hasDraftChanges: boolean;
  parentType: ScheduleDraftMode_ScheduleParentFragment["__typename"] | undefined;
};

function createColumns(opts: CreateColumnsOpts): GridColumn<DynamicSchedulesRow>[] {
  const { loading, enumDetails, groupBy, isLookaheadView, hasDraftChanges, parentType } = opts;
  const sortable = !isLookaheadView;
  return [
    ...(hasGroupRows(groupBy)
      ? [
          collapseColumn<DynamicSchedulesRow>({
            header: (_, { row }) => <CollapseToggle row={row} />,
            clientSideSort: false,
            w: "40px",
            group: (_, { row }) => ({ content: () => <CollapseToggle row={row} /> }),
            task: emptyCell,
            sticky: "left",
          }),
        ]
      : []),
    actionColumn<DynamicSchedulesRow>({
      clientSideSort: false,
      w: "48px",
      sticky: "left",
      header: emptyCell,
      lookahead: emptyCell,
      group: emptyCell,
      milestone: () => (
        <Tooltip title="Milestone">
          <LegacyBpIcon icon="signpost" color={Palette.Gray900} />
        </Tooltip>
      ),
      task: (task) =>
        loading ? (
          <Loader size="xs" />
        ) : task.isCriticalPath ? (
          <Icon icon="criticalPath" color={Palette.Gray900} inc={2.5} tooltip="Critical Path Task" />
        ) : (
          emptyCell
        ),
    }),
    column<DynamicSchedulesRow>({
      header: "Name",
      clientSideSort: sortable,
      mw: "240px",
      w: 2,
      sticky: "left",
      lookahead: ({ name, startDate, endDate }) => ({
        content: () => name,
        css: Css.lg.gray900.$,
        tooltip:
          name === "6 Weeks"
            ? `Tasks set to start on or after ${formatDate(startDate, "weekDayMonthShort")}`
            : `Tasks set to start between ${formatDate(startDate, "weekDayMonthShort")} - ${formatDate(endDate, "weekDayMonthShort")}`,
        value: name,
      }),
      group: ({ name, progress }) => ({
        content: () => (
          <div css={Css.df.gap2.$}>
            <span>{name}</span>
            {groupBy === TaskGroupBy.Stage && progress !== undefined && (
              <Tooltip title="This percentage reflects all tasks within the stage, not just those currently visible within the table.">
                <ProgressPill progress={progress} changeColorOnCompleted />
              </Tooltip>
            )}
          </div>
        ),
        typeScale: "smSb",
        colspan: 4,
        value: name,
      }),
      milestone: ({ name, progress }) => ({
        content: () => <TextTruncatedTooltip text={name} />,
        typeScale: "xsSb",
        value: name,
        sortValue: () => nameSortHelper({ name, progress }),
      }),
      task: (task) => ({
        content: () => <TextTruncatedTooltip text={task.name} />,
        value: task.name,
        sortValue: () => nameSortHelper({ name: task.name, status: task.status }),
      }),
    }),
    column<DynamicSchedulesRow>({
      header: "Trade",
      clientSideSort: sortable,
      mw: "200px",
      lookahead: emptyCell,
      group: emptyCell,
      milestone: ({ progress }) => ({
        colspan: 2,
        content: () => (
          <div css={Css.df.gap3.w100.$}>
            <div css={Css.df.$}>Milestone Completion:</div>
            <ProgressPill fixedWidthPx={150} changeColorOnCompleted progress={progress} />
          </div>
        ),
        value: undefined,
        sortValue: () => (progress === 100 ? "!" : "~"),
      }),
      task: (task) => ({
        content: () => (task.tradePartner ? <TextTruncatedTooltip text={task.tradePartner.name} /> : emptyCellDash),
        value: task.tradePartner?.name,
        sortValue: () => nameSortHelper({ name: task.tradePartner?.name ?? "-", status: task.status }),
      }),
    }),
    column<DynamicSchedulesRow>({
      header: "Trade Status",
      clientSideSort: sortable,
      mw: "140px",
      lookahead: emptyCell,
      group: emptyCell,
      milestone: ({ progress }) => ({
        content: () => <></>,
        value: "",
        sortValue: () => tradeStatusSortHelper({ progress }),
      }),
      task: (task) => ({
        content: () => (task.tradePartner ? <DraftTradeStatusTag task={task} /> : emptyCellDash),
        value: task.tradePartnerStatus.code,
        sortValue: () => tradeStatusSortHelper({ tradePartnerStatus: task.tradePartnerStatus, status: task.status }),
      }),
    }),
    column<DynamicSchedulesRow>({
      header: "Task Status",
      clientSideSort: sortable,
      mw: "140px",
      lookahead: emptyCell,
      group: emptyCell,
      milestone: ({ progress }) => ({
        content: () => <></>,
        value: "",
        sortValue: () =>
          enumDetails.taskStatus.find(({ code }) => code === TaskStatus.Complete)!.sortOrder *
          (progress === 100 ? 1 : -1),
      }),
      task: (task) => ({
        content: () => (
          <div css={Css.df.aic.gap1.$}>
            <StatusIndicator status={task.status.code} />
            {task.status.name}
          </div>
        ),
        sortValue: () => enumDetails.taskStatus.find(({ code }) => code === task.status.code)!.sortOrder,
      }),
    }),
    ...maybeRenderColumnForParentTypes(
      parentType,
      // Only projects have an assignee, not developments, schedule templates, etc.
      ["Community", "Market", "Subdivision", "Lot", "Structure", "StandaloneProject"],
      column<DynamicSchedulesRow>({
        header: "Assignee",
        clientSideSort: sortable,
        mw: "140px",
        lookahead: emptyCell,
        group: emptyCell,
        milestone: emptyCell,
        task: (task) => ({
          content: () => task.internalUser?.name ?? emptyCellDash,
          sortValue: () => task.internalUser?.name,
        }),
      }),
    ),
    column<DynamicSchedulesRow>({
      header: "Start",
      id: "start-date-column",
      clientSideSort: sortable,
      mw: "110px",
      group: emptyCell,
      lookahead: ({ startDate }) => ({
        content: () => <></>,
        // Sort the dividers by their start date, since the table starts sorted by this column
        sortValue: startDate,
      }),
      milestone: ({ estStartDate, progress }) => ({
        content: () => (estStartDate ? formatWithShortYear(estStartDate) : emptyCellDash),
        sortValue: () => dateSortHelper({ date: estStartDate, progress }),
      }),
      task: (task) => ({
        content: () => formatWithShortYear(task.startDate),
        sortValue: () => dateSortHelper({ date: task.startDate, status: task.status }),
      }),
    }),
    column<DynamicSchedulesRow>({
      header: "End",
      clientSideSort: sortable,
      mw: "110px",
      group: emptyCell,
      lookahead: emptyCell,
      milestone: ({ estEndDate, progress }) => ({
        content: () => (estEndDate ? formatWithShortYear(estEndDate) : emptyCellDash),
        sortValue: () => dateSortHelper({ date: estEndDate, progress }),
      }),
      task: (task) => ({
        content: () => formatWithShortYear(task.endDate),
        sortValue: () => dateSortHelper({ date: task.endDate, status: task.status }),
      }),
    }),
    column<DynamicSchedulesRow>({
      header: "Duration",
      clientSideSort: sortable,
      mw: "100px",
      group: emptyCell,
      lookahead: emptyCell,
      milestone: ({ durationInDays, progress }) => ({
        content: () => `${durationInDays} ${pluralize(durationInDays, "day", "days")}`,
        sortValue: () => durationSortHelper({ durationInDays, progress }),
      }),
      task: (task) => {
        const duration = task.knownDurationInDays ?? task.durationInDays;
        return {
          content: () => `${duration} ${pluralize(duration, "day", "days")}`,
          sortValue: () => durationSortHelper({ durationInDays: duration, status: task.status }),
        };
      },
    }),
    actionColumn<DynamicSchedulesRow>({
      header: emptyCell,
      clientSideSort: false,
      align: "center",
      w: "48px",
      sticky: "right",
      group: emptyCell,
      lookahead: emptyCell,
      milestone: (milestone) => <MilestoneActionCell milestone={milestone} hasDraftChanges={hasDraftChanges} />,
      task: (task) => <TaskMenuCell task={task} />,
    }),
  ];
}

type TaskMenuCellProps = { task: ScheduleDraftMode_PlanTaskFragment };

function TaskMenuCell(props: TaskMenuCellProps) {
  const { task } = props;
  const items = useDraftTaskMenuItems(task);
  return <ButtonMenu data-testid="draftTaskActions" trigger={{ icon: "verticalDots", inc: 2.3 }} items={items} />;
}

/** Adds each task's alerts as companion rows under it. */
function withAlertRows(rows: GridDataRow<DynamicSchedulesRow>[]): GridDataRow<DynamicSchedulesRow>[] {
  return rows.map((row) => {
    if (row.kind === "task") {
      const alerts = getDraftTaskAlerts(row.data);
      return alerts.nonEmpty ? { ...row, companion: <DraftTaskAlertBanners alerts={alerts} /> } : row;
    }
    return row.children ? { ...row, children: withAlertRows(row.children) } : row;
  });
}

type DraftTaskAlertBannersProps = { alerts: DraftTaskAlert[] };

function DraftTaskAlertBanners(props: DraftTaskAlertBannersProps) {
  const { alerts } = props;
  return (
    <div css={Css.df.fdc.gap1.$}>
      {alerts.map(({ type, tagText, description }) => (
        <InlineFeedbackBanner key={description} type={type} tagText={tagText} description={description} />
      ))}
    </div>
  );
}

/** Colors a task row by its worst alert. */
function taskRowCss(row: DynamicSchedulesTaskRow) {
  const alerts = getDraftTaskAlerts(row.data);
  if (alerts.some((a) => a.type === "error")) return Css.bgRed50.$;
  if (alerts.some((a) => a.type === "warning")) return Css.bgYellow50.$;
  return {};
}
